import {
    Button,
    Chip,
    Divider,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    useTheme,
} from '@material-ui/core'
import React, { useEffect, useMemo, useState } from 'react'
import VendorList from './VendorList'

import LoadingGIF from '../../../assets/Loading_Animation.gif'

import { makeStyles, createStyles } from '@material-ui/core/styles'
import { Container } from '../../../components'

import { useAppDispatch, useCompany, useService } from '../../../hooks'
import { axiosInstance } from '../../../helpers'
import { getServiceList } from '../../../store'
import { isNationalCatalogService, Service } from '../../../models'
import SearchIcon from '@material-ui/icons/Search'
import { VendorDetails } from './VendorDetails'
import { ArrowBack } from '@material-ui/icons'
import { useMultiSelectStyles } from '../../../styles'
import { Autocomplete } from '@material-ui/lab'
import {
    DEFAULT_NEARBY_RADIUS,
    LIST_MODE_ALL,
    LIST_MODE_FEATURED,
    LIST_MODE_NEARBY,
    LIST_MODE_RECOMMENDED,
    ListMode,
    NVLVendor,
} from './nvlVendorModel'
import { DEFAULT_NVL_PAGE_SIZE, useNVLVendors } from './useNVLVendors'

export type { NVLVendor } from './nvlVendorModel'
export { normalizeNVLVendor } from './nvlVendorModel'
export {
    DEFAULT_NVL_PAGE_SIZE,
    EMPTY_NVL_FILTERS,
    useNVLVendors,
} from './useNVLVendors'
export {
    NVL_FEATURED_HOME_LIMIT,
    useNVLFeaturedHome,
} from './useNVLFeaturedHome'
export type { FeaturedHomeVendor } from './useNVLFeaturedHome'
export { LIST_MODE_FEATURED } from './nvlVendorModel'

export interface NVLData {
    badges: JSX.Element
    organization: string
    services: JSX.Element
    complete: number
    goBack: number
    nvlVendor: NVLVendor
    action: JSX.Element
    users: number
    featuredVendor: boolean
    messageVendor: JSX.Element
}

const RADIUS_OPTIONS = [25, 50, 100, 150, 250, 500]

const customStyles = makeStyles(() =>
    createStyles({
        searchInput: {
            '& .MuiOutlinedInput-input': {
                padding: '9px 0 9px',
            },
        },
        filterChips: {
            background: '#F4F4F4',
            border: '1px solid #008C85',
            borderRadius: '29px',
            color: '#008C85',
            fontSize: '0.75rem',
            lineHeight: '14px',
            height: '24px',
            fontWeight: 600,
            marginRight: '5px',
            marginTop: '5px',
        },
        getQuoteBtn: {
            background: '#008C85',
            color: '#FFF',
            fontSize: '0.875rem',
            textTransform: 'inherit',
            fontWeight: 500,
            borderRadius: '5px',
        },
        spanReceived: {
            background: '#FFE29B',
            borderRadius: '5px',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#DFA804',
            width: '98px',
            height: '33px',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
        },
        spanSent: {
            background: '#ACE5CE',
            borderRadius: '5px',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#00B46A',
            width: '98px',
            height: '33px',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
        },
        actionMenu: {
            '& .MuiList-root': {
                width: 100,
            },
            '& .MuiMenuItem-root': {
                fontSize: '0.75rem',
                fontWeight: 400,
                color: '#000',
            },
        },
        serviceMenu: {
            '&.MuiList-root': {
                width: 150,
                maxHeight: '200px !important',
                overflow: 'auto',
            },
            '& .MuiListItemText-primary ': {
                fontSize: '0.75rem',
                fontWeight: 400,
                color: '#000',
            },
        },
        paper: {
            background: '#FFFFFF',
            border: '1px solid #000000',
            borderRadius: '5px',
            boxShadow:
                '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)',
        },
    }),
)

interface Props {
    location?: string
    radiusFilter?: number | null
    selectedService?: Service
    setSelectedService: (service: Service | undefined) => void
    setVendorSearchText?: (text: string) => void
    initialVendorSearch?: string
    initialVendorId?: number
    onBack?: () => void
}

interface VendorNameOption {
    id: number
    name: string
    workspace_count?: number
}

const formatVendorNameOption = (vendor: VendorNameOption): string => {
    const count = vendor.workspace_count ?? 0
    const label = count === 1 ? 'property' : 'properties'
    return `${vendor.name} (${count} ${label})`
}

const NationalVendorList = (props: Props) => {
    const {
        location,
        radiusFilter = null,
        selectedService,
        setSelectedService,
        setVendorSearchText,
        initialVendorSearch = '',
        initialVendorId,
        onBack,
    } = props

    const { requestUpdatedPricing } = useCompany()

    const classes = useMultiSelectStyles()

    const dispatch = useAppDispatch()

    const [selectedVendor, setSelectedVendor] = useState<
        NVLVendor | undefined
    >()

    const theme = useTheme()

    const [searchText, setSearchText] = useState(initialVendorSearch)
    const [listMode, setListMode] = useState<ListMode>(
        initialVendorSearch || initialVendorId
            ? LIST_MODE_ALL
            : LIST_MODE_RECOMMENDED,
    )
    const [listMessage, setListMessage] = useState<string | null>(null)
    const [page, setPage] = useState(1)
    const [
        recommendedFallbackMessage,
        setRecommendedFallbackMessage,
    ] = useState<string | null>(null)
    const [vendorNames, setVendorNames] = useState<VendorNameOption[]>([])
    const [openVendorDropDown, setOpenVendorDropDown] = useState(false)

    const { serviceList } = useService({ getServiceList: true, cleanUp: true })
    const nationalServiceList = useMemo(
        () => serviceList.filter(isNationalCatalogService),
        [serviceList],
    )

    const customClasses = customStyles()

    useEffect(() => {
        dispatch(getServiceList({}))
        axiosInstance
            .get<VendorNameOption[]>(
                `company/national_vendor_list/vendor_names/`,
            )
            .then((res) => {
                setVendorNames(res.data ?? [])
            })
            .catch(() => undefined)
    }, [])

    /******************************* FILTER DATA [START] ************************************** */
    const [filterData, setFilterData]: any = useState({
        searchText: initialVendorId ? '' : initialVendorSearch,
        vendorId: initialVendorId ?? null,
        services: selectedService ? [selectedService] : [],
        // null = All (no distance filter). Nearby mode will default to 100.
        radius: radiusFilter != null && radiusFilter > 0 ? radiusFilter : null,
        willingToTravel: 'all',
        trained: 'all',
        location: location ? location : '',
        minRating: null as number | null,
        sortBy: 'default' as 'default' | 'rating_desc' | 'rating_asc',
    })

    // Sync filters when navigating from home via URL params (service / vendor query).
    useEffect(() => {
        if (!selectedService) {
            return
        }
        setFilterData((prev: typeof filterData) => ({
            ...prev,
            services: [{ id: selectedService.id, name: selectedService.name }],
        }))
    }, [selectedService?.id])

    useEffect(() => {
        if (initialVendorId) {
            const matched = vendorNames.find((v) => v.id === initialVendorId)
            setSearchText(matched?.name ?? '')
            setListMode(LIST_MODE_ALL)
            setFilterData((prev: typeof filterData) => ({
                ...prev,
                searchText: '',
                vendorId: initialVendorId,
                services: [],
                radius: null,
                willingToTravel: 'all',
                trained: 'all',
            }))
            setSelectedService(undefined)
            return
        }
        if (!initialVendorSearch) {
            return
        }
        setSearchText(initialVendorSearch)
        setListMode(LIST_MODE_ALL)
        setFilterData((prev: typeof filterData) => ({
            ...prev,
            searchText: initialVendorSearch,
            vendorId: null,
            services: [],
            radius: null,
            willingToTravel: 'all',
            trained: 'all',
        }))
        setSelectedService(undefined)
    }, [initialVendorSearch, initialVendorId, vendorNames])

    const nvlFilters = useMemo(
        () => ({
            services: filterData.services as Array<{
                id: number
                name: string
            }>,
            trained: filterData.trained as string,
            willingToTravel: filterData.willingToTravel as string,
            radius:
                filterData.radius != null && Number(filterData.radius) > 0
                    ? Number(filterData.radius)
                    : null,
            searchText: (filterData.searchText || '') as string,
            vendorId: (filterData.vendorId as number | null) ?? null,
            minRating:
                filterData.minRating != null && Number(filterData.minRating) > 0
                    ? Number(filterData.minRating)
                    : null,
            sortBy: (filterData.sortBy || 'default') as
                | 'default'
                | 'rating_desc'
                | 'rating_asc',
        }),
        [
            filterData.services,
            filterData.trained,
            filterData.willingToTravel,
            filterData.radius,
            filterData.searchText,
            filterData.vendorId,
            filterData.minRating,
            filterData.sortBy,
        ],
    )

    const matchingVendors = useMemo(() => {
        const query = searchText.trim().toLocaleLowerCase()
        if (!query) {
            return []
        }
        return vendorNames
            .filter((vendor) => vendor.name.toLocaleLowerCase().includes(query))
            .slice(0, 20)
    }, [vendorNames, searchText])

    useEffect(() => {
        setPage(1)
    }, [listMode, nvlFilters])

    // Clear detail when the list context changes so an old vendor isn't shown.
    useEffect(() => {
        setSelectedVendor(undefined)
    }, [listMode, nvlFilters, page])

    const {
        vendors,
        count,
        pageSize,
        fallbackToNearby,
        fallbackMessage,
        emptyMessage: queryEmptyMessage,
        isFetching,
        isError,
    } = useNVLVendors(listMode, nvlFilters, page, DEFAULT_NVL_PAGE_SIZE)

    // Recommended → Nearby fallback when the recommended pool is empty / unavailable.
    useEffect(() => {
        if (!fallbackToNearby || listMode !== LIST_MODE_RECOMMENDED) {
            return
        }
        setRecommendedFallbackMessage(
            fallbackMessage ||
                'There are no recommendations in your area. Showing nearby vendors instead.',
        )
        setFilterData((prev: typeof filterData) => ({
            ...prev,
            radius:
                prev.radius != null && prev.radius > 0
                    ? prev.radius
                    : DEFAULT_NEARBY_RADIUS,
        }))
        setListMode(LIST_MODE_NEARBY)
    }, [fallbackToNearby, fallbackMessage, listMode])

    useEffect(() => {
        if (isFetching) {
            return
        }
        if (isError) {
            setListMessage('Unable to load vendors. Please try again.')
            return
        }
        if (queryEmptyMessage) {
            setListMessage(queryEmptyMessage)
            return
        }
        setListMessage(null)
    }, [isFetching, isError, queryEmptyMessage])

    // After a fetch completes, select the first vendor so the detail panel is discoverable.
    // Context changes clear selection first; keep a manual pick across background refetches.
    useEffect(() => {
        if (isFetching) {
            return
        }
        setSelectedVendor((current) => {
            if (vendors.length === 0) {
                return undefined
            }
            if (current && vendors.some((vendor) => vendor.id === current.id)) {
                return (
                    vendors.find((vendor) => vendor.id === current.id) ??
                    current
                )
            }
            return vendors[0]
        })
    }, [vendors, isFetching])

    const clearAllFilters = () => {
        setSelectedService(undefined)
        setSearchText('')
        setVendorSearchText?.('')
        setOpenVendorDropDown(false)
        setRecommendedFallbackMessage(null)
        setFilterData({
            searchText: '',
            vendorId: null,
            services: [],
            radius:
                listMode === LIST_MODE_NEARBY ? DEFAULT_NEARBY_RADIUS : null,
            willingToTravel: 'all',
            trained: 'all',
            location: location ? location : '',
            minRating: null,
            sortBy: 'default',
        })
    }

    const getEmptyListMessage = (): string => {
        const hasVendorId = filterData.vendorId != null
        const hasGlobalNameSearch =
            (filterData.searchText || '').trim().length > 0
        const hasServiceFilter = filterData.services.length > 0
        const hasRadiusFilter =
            filterData.radius != null && Number(filterData.radius) > 0
        const hasMinRating =
            filterData.minRating != null && Number(filterData.minRating) > 0

        if (hasVendorId) {
            return 'That vendor was not found.'
        }
        if (hasGlobalNameSearch) {
            return `No vendors matched “${filterData.searchText.trim()}”.`
        }
        if (hasServiceFilter) {
            return 'No vendors match the current filters. Try clearing a filter or switching modes.'
        }
        if (hasMinRating) {
            return `No vendors with a ${filterData.minRating}+ star rating match the current filters.`
        }
        if (hasRadiusFilter) {
            return 'No vendors found within this distance. Try increasing the radius or choose All.'
        }
        if (listMode === LIST_MODE_FEATURED) {
            return 'No featured vendors found right now.'
        }
        return 'No vendors to show in this list.'
    }

    const applyServiceFilter = (service?: Service) => {
        setSelectedService(service)
        setFilterData((prev: typeof filterData) => ({
            ...prev,
            services: service ? [{ id: service.id, name: service.name }] : [],
            // Changing service exits a global name-only search.
            searchText: '',
            vendorId: null,
        }))
    }

    const handleListModeChange = (mode: ListMode) => {
        setListMessage(null)
        // User-driven mode changes dismiss the Recommended→Nearby notice.
        setRecommendedFallbackMessage(null)
        setListMode(mode)
        setFilterData((prev: typeof filterData) => ({
            ...prev,
            // Mode changes clear a prior "Search all" name query.
            searchText: '',
            vendorId: null,
            // Nearby defaults to 100; Featured is distance-ordered with no radius cutoff.
            radius:
                mode === LIST_MODE_NEARBY
                    ? prev.radius != null && prev.radius > 0
                        ? prev.radius
                        : DEFAULT_NEARBY_RADIUS
                    : mode === LIST_MODE_FEATURED
                    ? null
                    : prev.radius,
        }))
    }

    const handleSelectVendor = (vendor: VendorNameOption) => {
        setOpenVendorDropDown(false)
        setSearchText(vendor.name)
        setVendorSearchText?.(vendor.name)
        setListMessage(null)
        setRecommendedFallbackMessage(null)
        setSelectedService(undefined)
        setListMode(LIST_MODE_ALL)
        setFilterData({
            searchText: '',
            vendorId: vendor.id,
            services: [],
            radius: null,
            willingToTravel: 'all',
            trained: 'all',
            location: location ? location : '',
            minRating: null,
            sortBy: 'default',
        })
    }

    const handleSearchAll = () => {
        const trimmed = searchText.trim()
        if (!trimmed) {
            return
        }
        setOpenVendorDropDown(false)
        setListMessage(null)
        setRecommendedFallbackMessage(null)
        setSelectedService(undefined)
        setListMode(LIST_MODE_ALL)
        setFilterData({
            searchText: trimmed,
            vendorId: null,
            services: [],
            radius: null,
            willingToTravel: 'all',
            trained: 'all',
            location: location ? location : '',
            minRating: null,
            sortBy: 'default',
        })
    }

    const listModePillStyle = (active: boolean): React.CSSProperties => ({
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 20,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        minWidth: 0,
        backgroundColor: active ? '#008C85' : theme.palette.grey[100],
        color: active ? 'white' : theme.palette.grey[800],
        border: active
            ? '1px solid #008C85'
            : `1px solid ${theme.palette.grey[300]}`,
    })

    const selectedVendorName =
        filterData.vendorId != null
            ? vendorNames.find((v) => v.id === filterData.vendorId)?.name ||
              searchText.trim()
            : ''

    const hasActiveFilterChips =
        filterData.services.length > 0 ||
        (filterData.searchText || '').trim().length > 0 ||
        filterData.vendorId != null ||
        filterData.willingToTravel !== 'all' ||
        filterData.trained !== 'all' ||
        (filterData.radius != null && Number(filterData.radius) > 0) ||
        (filterData.minRating != null && Number(filterData.minRating) > 0) ||
        (filterData.sortBy && filterData.sortBy !== 'default')
    /******************************************** FILTER DATA [END] *********************************** */

    const filteredVendors = vendors

    return (
        <>
            <Container
                style={{
                    height: 'calc(100vh - 104px)',
                    flex: 1,
                    width: '100%',
                    flexDirection: 'column',
                }}
                onClick={() => setOpenVendorDropDown(false)}
            >
                {/* Page header */}
                <Container direction="column" style={{ width: '100%' }}>
                    <Container
                        alignItems="center"
                        style={{
                            width: '100%',
                            padding: theme.spacing(1, 0, 1.5, 0),
                            gap: theme.spacing(2),
                        }}
                    >
                        <Container direction="column" style={{ minWidth: 220 }}>
                            <Container
                                style={{
                                    fontWeight: 700,
                                    fontSize: 28,
                                    color: '#000000',
                                    lineHeight: '32px',
                                }}
                            >
                                National Vendors List
                            </Container>
                            <Button
                                style={{
                                    alignSelf: 'flex-start',
                                    padding: 0,
                                    minWidth: 0,
                                    marginTop: theme.spacing(0.5),
                                    color: theme.palette.darkGreen.main,
                                    textTransform: 'none',
                                    fontSize: 13,
                                }}
                                onClick={() => onBack?.()}
                                startIcon={
                                    <ArrowBack style={{ fontSize: 16 }} />
                                }
                            >
                                Back to home
                            </Button>
                        </Container>
                    </Container>

                    <Divider style={{ width: '100%' }} />
                </Container>

                <Container
                    style={{
                        flex: 1,
                        minHeight: 0,
                        width: '100%',
                        flexDirection: 'row',
                        overflow: 'hidden',
                    }}
                >
                    <Container
                        style={{
                            height: '100%',
                            paddingTop: theme.spacing(2),
                            flexDirection: 'column',
                            maxWidth: 1320,
                            width: '100%',
                            flex: '1 1 1320px',
                            minWidth: 0,
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        {/* List toolbar */}
                        <Container
                            alignItems="center"
                            style={{
                                width: '100%',
                                marginBottom: theme.spacing(1.5),
                                gap: theme.spacing(1.5),
                                flexWrap: 'wrap',
                            }}
                        >
                            <Autocomplete
                                id="nvl-list-service-select"
                                options={nationalServiceList}
                                getOptionLabel={(option) => option.name}
                                getOptionSelected={(option, value) =>
                                    option.id === value.id
                                }
                                value={selectedService ?? null}
                                onChange={(_, value) => {
                                    applyServiceFilter(value ?? undefined)
                                }}
                                style={{ width: 220 }}
                                size="small"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Service"
                                        placeholder="Filter by service"
                                        size="small"
                                    />
                                )}
                            />

                            <Container
                                alignItems="center"
                                style={{
                                    border: `1px solid ${theme.palette.grey[400]}`,
                                    borderRadius: 4,
                                    backgroundColor: 'white',
                                    height: 40,
                                    position: 'relative',
                                    overflow: 'visible',
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <TextField
                                    variant="standard"
                                    placeholder="Search vendors by name"
                                    value={searchText}
                                    onChange={(e) => {
                                        setSearchText(e.target.value)
                                        setOpenVendorDropDown(true)
                                    }}
                                    onFocus={() => setOpenVendorDropDown(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault()
                                            handleSearchAll()
                                        }
                                    }}
                                    size="small"
                                    InputProps={{
                                        disableUnderline: true,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon
                                                    style={{
                                                        color:
                                                            theme.palette
                                                                .darkGreen.main,
                                                        marginLeft: theme.spacing(
                                                            1,
                                                        ),
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    style={{
                                        width: 380,
                                        padding: theme.spacing(0.5, 0),
                                    }}
                                />
                                {openVendorDropDown && searchText.trim() && (
                                    <Container
                                        direction="column"
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            width: '100%',
                                            maxHeight: 260,
                                            overflow: 'auto',
                                            backgroundColor: 'white',
                                            zIndex: 1000,
                                            borderRadius: 4,
                                            marginTop: 4,
                                            boxShadow:
                                                '0px 4px 12px rgba(0,0,0,0.12)',
                                        }}
                                    >
                                        {matchingVendors.length === 0 ? (
                                            <Container
                                                style={{
                                                    padding: theme.spacing(1.5),
                                                    color:
                                                        theme.palette.grey[600],
                                                    fontSize: 13,
                                                }}
                                            >
                                                No vendors found
                                            </Container>
                                        ) : (
                                            matchingVendors.map((vendor) => (
                                                <Button
                                                    key={vendor.id}
                                                    style={{
                                                        justifyContent:
                                                            'flex-start',
                                                        textTransform: 'none',
                                                        padding: theme.spacing(
                                                            1,
                                                            1.5,
                                                        ),
                                                        color:
                                                            theme.palette.text
                                                                .primary,
                                                        fontSize: 14,
                                                    }}
                                                    onClick={() =>
                                                        handleSelectVendor(
                                                            vendor,
                                                        )
                                                    }
                                                >
                                                    {formatVendorNameOption(
                                                        vendor,
                                                    )}
                                                </Button>
                                            ))
                                        )}
                                    </Container>
                                )}
                            </Container>
                            <Button
                                style={{
                                    color: 'white',
                                    backgroundColor:
                                        theme.palette.darkGreen.main,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                    height: 40,
                                }}
                                onClick={handleSearchAll}
                            >
                                Search all
                            </Button>

                            <Container flex={1} />

                            <Container
                                alignItems="center"
                                style={{
                                    gap: theme.spacing(1),
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Button
                                    style={listModePillStyle(
                                        listMode === LIST_MODE_RECOMMENDED,
                                    )}
                                    onClick={() =>
                                        handleListModeChange(
                                            LIST_MODE_RECOMMENDED,
                                        )
                                    }
                                >
                                    Recommended
                                </Button>
                                <Button
                                    style={listModePillStyle(
                                        listMode === LIST_MODE_NEARBY,
                                    )}
                                    onClick={() =>
                                        handleListModeChange(LIST_MODE_NEARBY)
                                    }
                                >
                                    Nearby
                                </Button>
                                <Button
                                    style={listModePillStyle(
                                        listMode === LIST_MODE_FEATURED,
                                    )}
                                    onClick={() =>
                                        handleListModeChange(LIST_MODE_FEATURED)
                                    }
                                >
                                    Featured
                                </Button>
                                <Button
                                    style={listModePillStyle(
                                        listMode === LIST_MODE_ALL,
                                    )}
                                    onClick={() =>
                                        handleListModeChange(LIST_MODE_ALL)
                                    }
                                >
                                    All
                                </Button>
                            </Container>
                        </Container>

                        {recommendedFallbackMessage && (
                            <Container
                                style={{
                                    width: '100%',
                                    justifyContent: 'flex-end',
                                    marginBottom: theme.spacing(1.5),
                                }}
                            >
                                <Container
                                    style={{
                                        width: 'fit-content',
                                        maxWidth: '100%',
                                        padding: theme.spacing(1, 1.5),
                                        backgroundColor: '#E8F5F4',
                                        border: '1px solid #008C85',
                                        borderRadius: 4,
                                        color: '#102A12',
                                        fontSize: 13,
                                        fontWeight: 500,
                                    }}
                                >
                                    {recommendedFallbackMessage}
                                </Container>
                            </Container>
                        )}

                        {/* Filter bar */}
                        <Container
                            alignItems="flex-end"
                            style={{
                                marginBottom: theme.spacing(1),
                                flexWrap: 'wrap',
                                gap: theme.spacing(1.5),
                            }}
                        >
                            <FormControl
                                className={classes.formControl}
                                style={{ minWidth: 120 }}
                            >
                                <InputLabel id="nvl-radius-label" shrink>
                                    Within
                                </InputLabel>
                                <Select
                                    labelId="nvl-radius-label"
                                    value={
                                        filterData.radius != null &&
                                        Number(filterData.radius) > 0
                                            ? Number(filterData.radius)
                                            : 'all'
                                    }
                                    onChange={(event) => {
                                        const value = event.target.value
                                        setFilterData({
                                            ...filterData,
                                            radius:
                                                value === 'all'
                                                    ? null
                                                    : Number(value),
                                        })
                                    }}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    {RADIUS_OPTIONS.map((miles) => (
                                        <MenuItem
                                            key={`RADIUS_${miles}`}
                                            value={miles}
                                        >
                                            {miles} mi
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl
                                className={classes.formControl}
                                style={{ minWidth: 110 }}
                            >
                                <InputLabel id="nvl-trained-label">
                                    Trained
                                </InputLabel>
                                <Select
                                    labelId="nvl-trained-label"
                                    value={filterData.trained}
                                    onChange={(event) => {
                                        setFilterData({
                                            ...filterData,
                                            trained: event.target
                                                .value as string,
                                        })
                                    }}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl
                                className={classes.formControl}
                                style={{ minWidth: 120 }}
                            >
                                <InputLabel id="nvl-travel-label">
                                    Will travel
                                </InputLabel>
                                <Select
                                    labelId="nvl-travel-label"
                                    value={filterData.willingToTravel}
                                    onChange={(event) => {
                                        setFilterData({
                                            ...filterData,
                                            willingToTravel: event.target
                                                .value as string,
                                        })
                                    }}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl
                                className={classes.formControl}
                                style={{ minWidth: 120 }}
                            >
                                <InputLabel id="nvl-min-rating-label" shrink>
                                    Rating
                                </InputLabel>
                                <Select
                                    labelId="nvl-min-rating-label"
                                    value={
                                        filterData.minRating != null &&
                                        Number(filterData.minRating) > 0
                                            ? Number(filterData.minRating)
                                            : 'all'
                                    }
                                    onChange={(event) => {
                                        const value = event.target.value
                                        setFilterData({
                                            ...filterData,
                                            minRating:
                                                value === 'all'
                                                    ? null
                                                    : Number(value),
                                        })
                                    }}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value={3}>3+ stars</MenuItem>
                                    <MenuItem value={4}>4+ stars</MenuItem>
                                    <MenuItem value={5}>5 stars</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl
                                className={classes.formControl}
                                style={{ minWidth: 140 }}
                            >
                                <InputLabel id="nvl-sort-label" shrink>
                                    Sort by
                                </InputLabel>
                                <Select
                                    labelId="nvl-sort-label"
                                    value={filterData.sortBy || 'default'}
                                    onChange={(event) => {
                                        setFilterData({
                                            ...filterData,
                                            sortBy: event.target
                                                .value as string,
                                        })
                                    }}
                                >
                                    <MenuItem value="default">Default</MenuItem>
                                    <MenuItem value="rating_desc">
                                        Highest rating
                                    </MenuItem>
                                    <MenuItem value="rating_asc">
                                        Lowest rating
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            {hasActiveFilterChips && (
                                <Container
                                    alignItems="center"
                                    style={{
                                        flexWrap: 'wrap',
                                        gap: theme.spacing(1),
                                    }}
                                >
                                    {filterData.services.map(
                                        (value: any, index: number) => (
                                            <Chip
                                                key={`serviceChip-${index}`}
                                                label={value.name}
                                                className={
                                                    customClasses.filterChips
                                                }
                                                onDelete={() => {
                                                    setSelectedService(
                                                        undefined,
                                                    )
                                                    setFilterData({
                                                        ...filterData,
                                                        services: filterData.services.filter(
                                                            (v: any) =>
                                                                v.id !==
                                                                value.id,
                                                        ),
                                                    })
                                                }}
                                            />
                                        ),
                                    )}

                                    {filterData.vendorId != null && (
                                        <Chip
                                            label={`Vendor: ${
                                                selectedVendorName ||
                                                filterData.vendorId
                                            }`}
                                            className={
                                                customClasses.filterChips
                                            }
                                            onDelete={() => {
                                                setSearchText('')
                                                setVendorSearchText?.('')
                                                setFilterData({
                                                    ...filterData,
                                                    searchText: '',
                                                    vendorId: null,
                                                })
                                            }}
                                        />
                                    )}

                                    {(filterData.searchText || '').trim() &&
                                        filterData.vendorId == null && (
                                            <Chip
                                                label={`Name: ${filterData.searchText.trim()}`}
                                                className={
                                                    customClasses.filterChips
                                                }
                                                onDelete={() => {
                                                    setSearchText('')
                                                    setVendorSearchText?.('')
                                                    setFilterData({
                                                        ...filterData,
                                                        searchText: '',
                                                        vendorId: null,
                                                    })
                                                }}
                                            />
                                        )}

                                    {filterData.radius != null &&
                                        Number(filterData.radius) > 0 && (
                                            <Chip
                                                label={`Within ${filterData.radius} mi`}
                                                className={
                                                    customClasses.filterChips
                                                }
                                                onDelete={() => {
                                                    setFilterData({
                                                        ...filterData,
                                                        radius: null,
                                                    })
                                                }}
                                            />
                                        )}

                                    {filterData.willingToTravel !== 'all' && (
                                        <Chip
                                            label={
                                                filterData.willingToTravel ===
                                                'yes'
                                                    ? 'Will travel'
                                                    : 'Will not travel'
                                            }
                                            className={
                                                customClasses.filterChips
                                            }
                                            onDelete={() => {
                                                setFilterData({
                                                    ...filterData,
                                                    willingToTravel: 'all',
                                                })
                                            }}
                                        />
                                    )}

                                    {filterData.trained !== 'all' && (
                                        <Chip
                                            label={
                                                filterData.trained === 'yes'
                                                    ? 'Trained'
                                                    : 'Not trained'
                                            }
                                            className={
                                                customClasses.filterChips
                                            }
                                            onDelete={() => {
                                                setFilterData({
                                                    ...filterData,
                                                    trained: 'all',
                                                })
                                            }}
                                        />
                                    )}

                                    {filterData.minRating != null &&
                                        Number(filterData.minRating) > 0 && (
                                            <Chip
                                                label={
                                                    Number(
                                                        filterData.minRating,
                                                    ) >= 5
                                                        ? '5 stars'
                                                        : `${filterData.minRating}+ stars`
                                                }
                                                className={
                                                    customClasses.filterChips
                                                }
                                                onDelete={() => {
                                                    setFilterData({
                                                        ...filterData,
                                                        minRating: null,
                                                    })
                                                }}
                                            />
                                        )}

                                    {filterData.sortBy &&
                                        filterData.sortBy !== 'default' && (
                                            <Chip
                                                label={
                                                    filterData.sortBy ===
                                                    'rating_desc'
                                                        ? 'Highest rating'
                                                        : 'Lowest rating'
                                                }
                                                className={
                                                    customClasses.filterChips
                                                }
                                                onDelete={() => {
                                                    setFilterData({
                                                        ...filterData,
                                                        sortBy: 'default',
                                                    })
                                                }}
                                            />
                                        )}

                                    <Button
                                        style={{
                                            color: theme.palette.darkGreen.main,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            minWidth: 0,
                                        }}
                                        onClick={clearAllFilters}
                                    >
                                        Clear
                                    </Button>
                                </Container>
                            )}
                        </Container>

                        {listMessage && (
                            <Container
                                style={{
                                    marginBottom: theme.spacing(1),
                                    padding: theme.spacing(1, 1.5),
                                    backgroundColor: theme.palette.grey[100],
                                    borderRadius: 4,
                                    color: theme.palette.grey[800],
                                    fontSize: 13,
                                }}
                            >
                                {listMessage}
                            </Container>
                        )}

                        <Container
                            style={{
                                position: 'relative',
                                flex: 1,
                                minHeight: 0,
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}
                        >
                            {isFetching ? (
                                <Container
                                    style={{
                                        flex: 1,
                                        width: '100%',
                                        height: '100%',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <img src={LoadingGIF} alt="Loading" />
                                </Container>
                            ) : (
                                <VendorList
                                    setSelectedVendor={setSelectedVendor}
                                    vendors={filteredVendors}
                                    count={count}
                                    page={page}
                                    pageSize={pageSize}
                                    onPageChange={setPage}
                                    emptyMessage={getEmptyListMessage()}
                                    selectedVendor={selectedVendor}
                                    requestUpdatedPricing={
                                        requestUpdatedPricing
                                    }
                                />
                            )}
                        </Container>
                    </Container>
                    <Container
                        style={{
                            minWidth: 440,
                            flex: 1,
                            height: '100%',
                            minHeight: 0,
                        }}
                    >
                        <Divider
                            orientation="vertical"
                            style={{ margin: theme.spacing(0, 2, 0, 2) }}
                        />
                        <Container
                            style={{
                                flex: 1,
                                minWidth: 0,
                                height: '100%',
                                justifyContent: 'center',
                            }}
                        >
                            {selectedVendor && (
                                <VendorDetails
                                    selectedVendor={selectedVendor}
                                />
                            )}
                        </Container>
                    </Container>
                </Container>
            </Container>
        </>
    )
}

export default NationalVendorList
